import { useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";

/**
 * Hvem som skriver en loggpost akkurat nå.
 *
 * KRINGKASTES, LAGRES ALDRI. En skriveindikator er sann i to sekunder og
 * uinteressant etterpå. Skrevet til basen ville den blitt en rad per
 * tastetrykk i en logg som er append-only — og tusen rader om dagen som
 * ingen skal lese.
 *
 * Derfor Realtime `broadcast`: meldingen går fra telefon til skjerm uten å
 * ta veien om en tabell, og den som ikke er på kanalen i øyeblikket får den
 * aldri. Det er riktig for noe som uansett bare betyr noe mens det skjer.
 *
 * ÉN KANAL PER PRODUKSJON OG OPPTAKSDAG. Tre regier på dag 7 er på samme
 * kanal og sender hver sin økt-id; redaktøren vet da hvilken spalte
 * prikkene hører til.
 *
 * FALLER AV SEG SELV. Appen sender «skriver» mens det tastes, og slutter
 * uten å si fra når telefonen legges bort eller dekningen ryker. Derfor en
 * levetid her: står det stille lenger enn det, er prikkene borte. Å stole
 * på en avsluttende melding ville gitt prikker som aldri forsvant.
 */

export const KANAL = (produksjonId: string, opptaksdag: string): string =>
  `skriver:${produksjonId}:${opptaksdag}`;

export const HENDELSE = "skriver";

/** Hvor lenge en melding gjelder. To sendinger rekker alltid innenfor. */
const LEVETID_MS = 4000;

export interface Skrivemelding {
  readonly oektId: string;
}

export function useSkrivende(
  produksjonId: string, opptaksdag: string,
): ReadonlySet<string> {
  const [, tikk] = useState(0);
  /* Økt-id → når meldingen går ut. Lest ved tegning, ikke tilstand i seg
     selv: den endres oftere enn flata skal tegnes. */
  const sett = useRef(new Map<string, number>());

  useEffect(() => {
    sett.current = new Map();
    if (supabase === null) return;

    const kanal = supabase.channel(KANAL(produksjonId, opptaksdag), {
      config: { broadcast: { self: false } },
    });

    kanal.on("broadcast", { event: HENDELSE }, (melding) => {
      const id = (melding["payload"] as Skrivemelding | undefined)?.oektId;
      if (typeof id !== "string") return;
      sett.current.set(id, Date.now() + LEVETID_MS);
      tikk((n) => n + 1);
    });
    kanal.subscribe();

    /* Rydder ut dem som har gått ut. Ett sekund er tett nok til at prikkene
       forsvinner når skrivingen stopper, og billig nok til å la gå. */
    const id = window.setInterval(() => {
      const naa = Date.now();
      let endret = false;
      for (const [k, til] of sett.current) {
        if (til <= naa) { sett.current.delete(k); endret = true; }
      }
      if (endret) tikk((n) => n + 1);
    }, 1000);

    return () => {
      window.clearInterval(id);
      void supabase?.removeChannel(kanal);
    };
  }, [produksjonId, opptaksdag]);

  const naa = Date.now();
  const levende = new Set<string>();
  for (const [k, til] of sett.current) if (til > naa) levende.add(k);
  return levende;
}
